import { ReactElement } from 'react';
import { Field, useFormikContext } from 'formik';
import { FormType } from '@constants/forms';
import { TileRadio, MultiTextInput, DateInput } from '@components';
import { tile } from '@interfaces/interfaces';
import { iThirdPartyFormValues } from '@services/ThirdPartyService';
import { nextDay } from '@constants';
import gridStyle from '@pages/forms/Grid.module.scss';
import styles from './TPStep2.module.scss';

function TPStep2(): ReactElement {
  const FinesAuthorisationBody = (): ReactElement => (
    <div>
      <p className={styles.normalFontWeight}>
        <strong>All</strong>
        {' '}
        the following fines
      </p>
      <p className={styles.normalFontWeightBelow}>please list below</p>
    </div>
  );

  const FinesExceptAuthorisationBody = (): ReactElement => (
    <div>
      <p className={styles.normalFontWeight}>
        All fines,
        {' '}
        <strong>except</strong>
        {' '}
        the following
      </p>
      <p className={styles.normalFontWeightBelow}>please list below</p>
    </div>
  );

  const TILE_DATA_PROVIDE_AUTHORISATION: tile[] = [
    {
      body: 'Yes',
      value: 'Yes',
    },
    {
      body: 'No',
      value: 'No',
    },
  ];

  const TILE_DATA_FINES_PROVIDE_AUTHORISATION: tile[] = [
    {
      body: <FinesAuthorisationBody />,
      value: 'All the following fines',
    },
    {
      body: <FinesExceptAuthorisationBody />,
      value: 'All fines, expect the following',
    },
  ];

  const TILE_DATA_AUTH_LAST: tile[] = [
    {
      body: 'Ongoing',
      value: 'Ongoing',
    },
    {
      body: 'Until a specific date',
      value: 'Date',
    },
  ];
  const { values }: { values: iThirdPartyFormValues } = useFormikContext();

  const renderYesNo = (): boolean | null => {
    if (!values.WhatFinesDoesThisAuthorisationCover) {
      return null;
    }
    if (values.WhatFinesDoesThisAuthorisationCover === 'Yes') {
      return true;
    }
    return false;
  };

  const allFinesIncluded = (): boolean | null => {
    // No options selected
    if (!values.authexclude) {
      return null;
    }
    // Fines included
    if (values.authexclude === 'All the following fines') {
      return false;
    }
    // Fines excluded
    return true;
  };

  return (
    <div className={gridStyle.form}>
      <div className="vfH2">What are you authorising?</div>
      <div className={styles.vfH3}>
        Would you like to provide authorisation for
        {' '}
        <b><i>all</i></b>
        {' '}
        current and future fines?
      </div>
      <Field name="WhatFinesDoesThisAuthorisationCover" component={TileRadio} tileData={TILE_DATA_PROVIDE_AUTHORISATION} orientationRow />
      {renderYesNo() === false && (
        <>
          <p className={styles.noMargin}>
            Your authorised person can only act on the fines you include here. If the authorisation does not cover all your fines it may affect what the person can do on your
            behalf. For more information see
            {' '}
            <a href="https://online.fines.vic.gov.au/Support/Authorise-another-person-to-help-manage-your-fines" target="_blank" aria-label="link to" rel="noreferrer">
              Third party authorisation
            </a>
            .
          </p>
          <div className={styles.vfH3}>Which fines would you like to provide authorisation for?</div>
          <Field name="authexclude" component={TileRadio} tileData={TILE_DATA_FINES_PROVIDE_AUTHORISATION} orientationRow mobileCollapseRow />
          {values.authexclude && (
            <>
              {' '}
              <div>
                {allFinesIncluded() ? (
                  <p className={styles.noMargin}>
                    Fines
                    {' '}
                    <strong>not to be included</strong>
                  </p>
                ) : (
                  <p className={styles.noMargin}>
                    Fines to
                    {' '}
                    <strong>be included</strong>
                  </p>
                )}
              </div>
              <MultiTextInput name="obligations" formName={FormType.THIRDPARTY} stepNo="step2" />
            </>
          )}
        </>
      )}
      {(renderYesNo() || values.authexclude) && (
        <>
          <div className={styles.vfH3}>How long would you like this authorisation to last?</div>
          <Field name="HowLongWillThisAuthorisationLast" component={TileRadio} tileData={TILE_DATA_AUTH_LAST} orientationRow />
          <p className={styles.noMargin}>
            <strong>Please note: </strong>
            You, or the person you give authority to, can cancel an authority at any time by contacting Fines Victoria
          </p>
          {values.HowLongWillThisAuthorisationLast === 'Date' && <Field name="authlast" title="Date" component={DateInput} minDate={nextDay} isMandatory />}
        </>
      )}
    </div>
  );
}

export default TPStep2;
